/**
 * Media placeholder tags.
 * Format: {{asset:<tag>|<type>|<description>}}
 */

import {
  collectMediaUrls,
  type MediaFieldPredicate,
} from "@peekie/case-spec";

export const PLACEHOLDER_REGEX =
  /\{\{asset:([a-z0-9_]+)\|(image|audio|video|document)\|([^}|]+)\}\}/g;

export const PLACEHOLDER_EXACT =
  /^\{\{asset:([a-z0-9_]+)\|(image|audio|video|document)\|([^}|]+)\}\}$/;

export type PlaceholderType = "image" | "audio" | "video" | "document";

export type ParsedPlaceholder = {
  tag: string;
  type: PlaceholderType;
  description: string;
  raw: string;
};

export function makePlaceholder(
  tag: string,
  type: PlaceholderType,
  description: string,
): string {
  const safeTag = tag.replace(/[^a-z0-9_]/g, "_").toLowerCase();
  const safeDesc = description.replace(/[|}]/g, "/").trim();
  return `{{asset:${safeTag}|${type}|${safeDesc}}}`;
}

export function parsePlaceholders(text: string): ParsedPlaceholder[] {
  const out: ParsedPlaceholder[] = [];
  const re = new RegExp(PLACEHOLDER_REGEX.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    out.push({
      tag: m[1],
      type: m[2] as PlaceholderType,
      description: m[3],
      raw: m[0],
    });
  }
  return out;
}

export function hasPlaceholders(value: unknown): boolean {
  return countPlaceholders(value) > 0;
}

export function countPlaceholders(value: unknown): number {
  return collectPlaceholderStrings(value).length;
}

export function collectPlaceholderStrings(value: unknown): string[] {
  const found: string[] = [];
  walk(value, (s) => {
    const re = new RegExp(PLACEHOLDER_REGEX.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(s)) !== null) {
      found.push(m[0]);
    }
  });
  return found;
}

/** Unique placeholders by tag (first description wins). */
export function collectUniquePlaceholders(
  value: unknown,
): ParsedPlaceholder[] {
  const byTag = new Map<string, ParsedPlaceholder>();
  for (const raw of collectPlaceholderStrings(value)) {
    const parsed = parsePlaceholders(raw);
    for (const p of parsed) {
      if (!byTag.has(p.tag)) byTag.set(p.tag, p);
    }
  }
  return [...byTag.values()];
}

/**
 * Replace placeholder tags with public URLs.
 * `urlByTag` maps placeholder tag → https URL.
 */
export function rewritePlaceholders(
  value: unknown,
  urlByTag: Map<string, string> | Record<string, string>,
): unknown {
  const map =
    urlByTag instanceof Map
      ? urlByTag
      : new Map(Object.entries(urlByTag));
  return rewriteWalk(value, map);
}

function rewriteWalk(
  value: unknown,
  urlByTag: Map<string, string>,
): unknown {
  if (typeof value === "string") {
    return value.replace(
      new RegExp(PLACEHOLDER_REGEX.source, "g"),
      (full, tag: string) => urlByTag.get(tag) ?? full,
    );
  }
  if (Array.isArray(value)) {
    return value.map((item) => rewriteWalk(item, urlByTag));
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = rewriteWalk(v, urlByTag);
    }
    return out;
  }
  return value;
}

function walk(value: unknown, onString: (s: string) => void): void {
  if (typeof value === "string") {
    onString(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) walk(item, onString);
    return;
  }
  if (value && typeof value === "object") {
    for (const v of Object.values(value as Record<string, unknown>)) {
      walk(v, onString);
    }
  }
}

export type MediaFieldRef = {
  path: string;
  field: string;
  value: string;
};

/**
 * Collect non-empty media fields (same rules as case-spec media collection).
 * Includes string arrays such as safari page hero_image_urls.
 */
export function collectMediaFields(
  value: unknown,
  basePath = "",
  isMediaField?: MediaFieldPredicate,
): MediaFieldRef[] {
  return collectMediaUrls(value, basePath, isMediaField).map((ref) => ({
    path: ref.path,
    field: ref.field,
    value: ref.url,
  }));
}

function mediaValuePreview(value: string): string {
  return value.length > 80 ? `${value.slice(0, 80)}…` : value;
}

/** Pipeline phone draft: every media field value must be a full {{asset:...}} placeholder. */
export function checkMediaFieldsArePlaceholders(
  bundle: unknown,
  isMediaField?: MediaFieldPredicate,
): {
  ok: boolean;
  errors: Array<{ path: string; message: string }>;
} {
  const errors: Array<{ path: string; message: string }> = [];
  for (const ref of collectMediaFields(bundle, "", isMediaField)) {
    const placeholder = parseExactPlaceholder(ref.value);
    if (!placeholder) {
      errors.push({
        path: ref.path,
        message: `expected media placeholder {{asset:tag|type|description}}, got "${mediaValuePreview(ref.value)}"`,
      });
      continue;
    }
    if (requiresDocumentPlaceholder(ref.field) && placeholder.type !== "document") {
      errors.push({
        path: ref.path,
        message: `expected document placeholder, got "${placeholder.type}"`,
      });
    }
  }
  return { ok: errors.length === 0, errors };
}

const HTTPS_URL = /^https:\/\//i;

export function collectMediaFieldValues(
  bundle: unknown,
  isMediaField?: MediaFieldPredicate,
): Set<string> {
  return new Set(
    collectMediaFields(bundle, "", isMediaField).map((ref) => ref.value),
  );
}

/**
 * Enrich runs after gen_assets, so baseline media is already https.
 * Keep those URLs. New media must still be a placeholder.
 */
export function checkEnrichMediaFields(
  bundle: unknown,
  baseline: unknown,
  isMediaField?: MediaFieldPredicate,
): {
  ok: boolean;
  errors: Array<{ path: string; message: string }>;
} {
  const baselineValues = collectMediaFieldValues(baseline, isMediaField);
  const errors: Array<{ path: string; message: string }> = [];
  for (const ref of collectMediaFields(bundle, "", isMediaField)) {
    const placeholder = parseExactPlaceholder(ref.value);
    if (placeholder) {
      if (
        !requiresDocumentPlaceholder(ref.field) ||
        placeholder.type === "document"
      ) {
        continue;
      }
      errors.push({
        path: ref.path,
        message: `expected document placeholder, got "${placeholder.type}"`,
      });
      continue;
    }
    if (HTTPS_URL.test(ref.value) && baselineValues.has(ref.value)) continue;
    const preview = mediaValuePreview(ref.value);
    errors.push({
      path: ref.path,
      message: HTTPS_URL.test(ref.value)
        ? `new media must be {{asset:tag|type|description}} placeholder, got "${preview}"`
        : `expected media placeholder {{asset:tag|type|description}}, got "${preview}"`,
    });
  }
  return { ok: errors.length === 0, errors };
}

function requiresDocumentPlaceholder(field: string): boolean {
  return field === "url" || field === "file_url";
}

function parseExactPlaceholder(value: string): ParsedPlaceholder | null {
  const match = value.match(PLACEHOLDER_EXACT);
  if (!match) return null;
  return {
    tag: match[1]!,
    type: match[2] as PlaceholderType,
    description: match[3]!,
    raw: match[0],
  };
}

/**
 * Rewrite placeholders to dummy https media URLs so validateCaseBundle can
 * check published-shape rules (e.g. meta.cover media_url) during phone draft.
 */
export function rewritePlaceholdersForStructuralValidate(
  value: unknown,
): unknown {
  const unique = collectUniquePlaceholders(value);
  if (unique.length === 0) return value;
  const urlByTag = new Map<string, string>();
  for (const p of unique) {
    urlByTag.set(
      p.tag,
      `https://cdn.peekie.ai/api/media/assets/_validate_${p.tag}`,
    );
  }
  return rewritePlaceholders(value, urlByTag);
}
